import { Box } from '@mui/material';
import React, { useCallback } from 'react';
import { DragDropContext, Droppable, DropResult } from '@hello-pangea/dnd';

import { ReorderableBlockListItem } from './ReorderableBlockListItem';
import { styles } from './ReorderableBlockList.styles';

/**
 * Local replacement for `@opengov/component-library/capital/ReorderableBlockList`.
 *
 * The upstream component is built on `react-beautiful-dnd@10.x` which is
 * unmaintained and broken under React 18 + the available test utilities. This
 * wrapper uses `@hello-pangea/dnd` (the maintained fork with React 18 support)
 * but keeps the exact same public API (`onDragEnd(sourceIndex, destinationIndex,
 * sourceId)` + `<ReorderableBlockList.Item>` children) so consumers don't need
 * to change.
 *
 * Delete this local wrapper and revert to the upstream component once
 * `@opengov/component-library` migrates to `@hello-pangea/dnd` internally.
 */
export type IReorderableBlockListProps = {
  dataTest?: string;
  /** Callback function when the user finishes dragging an item. */
  onDragEnd?: (sourceIndex: number, destinationIndex: number, sourceId: string) => void;
  children?: React.ReactNode;
};

type ReorderableBlockListComponent = React.FC<IReorderableBlockListProps> & {
  Item: typeof ReorderableBlockListItem;
};

export const ReorderableBlockList: ReorderableBlockListComponent = ({ dataTest, onDragEnd, children }) => {
  const handleDragEnd = useCallback(
    (result: DropResult) => {
      const { destination, source, draggableId } = result;
      if (!destination || destination.index === source.index) {
        return;
      }
      onDragEnd?.(source.index, destination.index, draggableId);
    },
    [onDragEnd]
  );

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId="droppable">
        {(provided, { isDraggingOver }) => (
          <Box
            {...provided.droppableProps}
            ref={provided.innerRef}
            data-test={dataTest}
            sx={isDraggingOver ? { ...styles.container, ...styles.active } : styles.container}
          >
            {React.Children.map(children, (child, index) =>
              React.isValidElement(child)
                ? React.cloneElement(
                    child as React.ReactElement<{ index: number; dataTest: string; isDraggingOver: boolean }>,
                    {
                      index,
                      dataTest: `list-item-${index}`,
                      isDraggingOver
                    }
                  )
                : child
            )}
            {provided.placeholder}
          </Box>
        )}
      </Droppable>
    </DragDropContext>
  );
};

ReorderableBlockList.Item = ReorderableBlockListItem;
